import { useCallback, useMemo, useState } from 'react';

import { Badge } from '@bambi/component-library/src/components/Badge/Badge';
import { Button } from '@bambi/component-library/src/components/Button/Button';
import {
  AdjustmentsHorizontalIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { formatTimeFrom24HourTo12Hour } from 'src/common/date/formatTimeFrom24HourTo12Hour';
import { FilterPopover } from 'src/common/primitives/FilterPopover/FilterPopover';
import { FilterCountBadge } from 'src/common/primitives/FilterPopover/FilterPopoverTrigger';
import { Tag } from 'src/common/tags/Tag/Tag';
import AccordionFilterItem from 'src/features/dispatch/common/AccordionFilterItem';
import { TagFilter } from 'src/features/dispatch/common/TagFilter';
import {
  initialState,
  setUnassignedTripsFilters,
} from 'src/features/dispatch/dispatcher.slice';
import { TripSpaceTypeBadge } from 'src/features/trip/management/TripSpaceTypeBadge';

import { CountiesFilter } from './CountiesFilter';
import { PayersFilter } from './PayersFilter';
import { ServiceTypeFilter } from './ServiceTypeFilter';
import { SpaceTypeFilter } from './SpaceTypeFilter';
import { TimeRange, TimeRangeFilter } from './TimeRangeFilter';
import { ZipCodesFilter } from './ZipCodesFilter';

export function UnassignedTripsFilters() {
  const unassignedTripsFilters = useAppSelector(
    (state) => state.dispatcher.unassignedTripsFilters
  );

  const dispatch = useAppDispatch();

  const [open, setOpen] = useState(false);

  const [filterState, setFilterState] = useState<typeof unassignedTripsFilters>(
    {
      ...unassignedTripsFilters,
    }
  );

  const timeRange = useMemo(
    () => ({
      startTime: filterState.startTime,
      endTime: filterState.endTime,
    }),
    [filterState.startTime, filterState.endTime]
  );

  const selectedPayers = useMemo(
    () => filterState.payers,
    [filterState.payers]
  );

  const selectedSpaceTypes = useMemo(
    () => filterState.spaceTypes,
    [filterState.spaceTypes]
  );

  const selectedServiceTypes = useMemo(
    () => filterState.serviceTypes,
    [filterState.serviceTypes]
  );

  const selectedZipCodes = useMemo(
    () => filterState.zipCodes,
    [filterState.zipCodes]
  );

  const selectedCounties = useMemo(
    () => filterState.counties,
    [filterState.counties]
  );

  const selectedTags = useMemo(() => filterState.tags, [filterState.tags]);

  const numFiltersApplied = useMemo(() => {
    return (
      (filterState.startTime || filterState.endTime ? 1 : 0) +
      filterState.payers.length +
      filterState.spaceTypes.length +
      filterState.serviceTypes.length +
      filterState.zipCodes.length +
      filterState.counties.length +
      filterState.tags.length
    );
  }, [filterState]);

  const resetInternalFilterStateToInitialState = useCallback(() => {
    setFilterState(initialState.unassignedTripsFilters);
  }, []);

  const onPopoverApply = useCallback(() => {
    dispatch(setUnassignedTripsFilters(filterState));
    setOpen(false);
  }, [dispatch, filterState]);

  const onPopoverClearFilters = useCallback(() => {
    resetInternalFilterStateToInitialState();
    dispatch(setUnassignedTripsFilters(initialState.unassignedTripsFilters));
    setOpen(false);
  }, [dispatch, resetInternalFilterStateToInitialState]);

  return (
    <FilterPopover
      open={open}
      onOpenChange={setOpen}
      onApply={onPopoverApply}
      onCancel={onPopoverClearFilters}
      applyButtonText="Apply Filters"
      cancelButtonText="Clear Filters"
      contentClassName="flex flex-col justify-between px-1"
      triggerElement={
        <Button dataTestId="unassigned-trip-time-filter-trigger">
          <AdjustmentsHorizontalIcon className="mr-2 h-4 w-4" />
          Filters
          <div className="flex items-center gap-1">
            {numFiltersApplied > 0 && (
              <FilterCountBadge value={numFiltersApplied} />
            )}
          </div>
        </Button>
      }
    >
      <div className="mb-4 flex min-w-[300px] max-w-[80vw] flex-col gap-2 md:max-w-[30vw]">
        {numFiltersApplied > 0 && (
          <>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">Applied Filters</h3>
              <Button
                variant="ghost"
                onClick={resetInternalFilterStateToInitialState}
                className="text-xs"
                dataTestId="clear-all-dispatch-calendar-filters-button"
                disabled={numFiltersApplied === 0}
              >
                Clear All
              </Button>
            </div>
            <div
              data-testid="unassigned-trips-applied-filters"
              className="flex flex-wrap gap-2"
            >
              {timeRange.startTime && (
                <Badge
                  label={`Time Range Start: ${formatTimeFrom24HourTo12Hour(
                    timeRange.startTime
                  )}`}
                  color="gray"
                  closeable
                  onClose={() =>
                    setFilterState((prev) => ({ ...prev, startTime: '' }))
                  }
                  testId="time-range-start-badge"
                />
              )}
              {timeRange.endTime && (
                <Badge
                  label={`Time Range End: ${formatTimeFrom24HourTo12Hour(
                    timeRange.endTime
                  )}`}
                  color="gray"
                  closeable
                  onClose={() =>
                    setFilterState((prev) => ({ ...prev, endTime: '' }))
                  }
                  testId="time-range-end-badge"
                />
              )}
              {selectedPayers.map((payer) => (
                <Badge
                  key={payer.id}
                  label={`Payer: ${payer.display_name}`}
                  color="yellow"
                  closeable
                  onClose={() =>
                    setFilterState((prev) => ({
                      ...prev,
                      payers: selectedPayers.filter((p) => p.id !== payer.id),
                    }))
                  }
                  testId={`payer-${payer.id}-badge`}
                />
              ))}
              {selectedSpaceTypes.map((spaceType) => (
                <TripSpaceTypeBadge
                  key={spaceType}
                  spaceType={spaceType}
                  closeable
                  onClose={() =>
                    setFilterState((prev) => ({
                      ...prev,
                      spaceTypes: selectedSpaceTypes.filter(
                        (s) => s !== spaceType
                      ),
                    }))
                  }
                />
              ))}
              {selectedServiceTypes.map((serviceType) => (
                <Badge
                  key={serviceType}
                  label={serviceType}
                  color="green"
                  closeable
                  onClose={() =>
                    setFilterState((prev) => ({
                      ...prev,
                      serviceTypes: selectedServiceTypes.filter(
                        (s) => s !== serviceType
                      ),
                    }))
                  }
                  testId={`service-type-${serviceType}-badge`}
                />
              ))}
              {selectedZipCodes.map((zipCode) => (
                <Badge
                  key={zipCode}
                  label={`Zip Code: ${zipCode}`}
                  color="blue-dark"
                  closeable
                  onClose={() =>
                    setFilterState((prev) => ({
                      ...prev,
                      zipCodes: selectedZipCodes.filter((z) => z !== zipCode),
                    }))
                  }
                  testId={`zip-code-${zipCode}-badge`}
                />
              ))}
              {selectedCounties.map((county) => (
                <Badge
                  key={`${county.state}:${county.county}`}
                  label={`County: ${county.county}, ${county.state}`}
                  color="blue"
                  closeable
                  onClose={() =>
                    setFilterState((prev) => ({
                      ...prev,
                      counties: selectedCounties.filter(
                        (c) =>
                          !(
                            c.state === county.state &&
                            c.county === county.county
                          )
                      ),
                    }))
                  }
                  testId={`county-${county.state}-${county.county}-badge`}
                />
              ))}
              {selectedTags.map((tag) => (
                <Tag
                  key={tag.id}
                  label={`Trip Tag: ${tag.tag_name}`}
                  color={tag.color_hex || '#DDD'}
                  onClick={() => {
                    setFilterState((prev) => ({
                      ...prev,
                      tags: selectedTags.filter((t) => t.id !== tag.id),
                    }));
                  }}
                  icon={
                    <XMarkIcon
                      className="h-4 w-4 cursor-pointer"
                      data-testid={`delete-trip-tag-${tag.id}`}
                    />
                  }
                  data-testid={`trip-tag-${tag.id}`}
                />
              ))}
            </div>
          </>
        )}
        <div className="flex max-h-[60vh] flex-col gap-2 overflow-y-auto">
          <AccordionFilterItem
            itemName="time-range"
            label="Time Range"
            badgeLabel={formatTimeRangeFilterValue(timeRange)}
          >
            <TimeRangeFilter
              onTimeRangeChange={(fieldKey: string, value: string) =>
                setFilterState((prev) => ({ ...prev, [fieldKey]: value }))
              }
              timeRange={timeRange}
            />
          </AccordionFilterItem>
          <AccordionFilterItem
            itemName="payer"
            label="Payers"
            badgeLabel={selectedPayers.length}
          >
            <PayersFilter
              selectedPayers={selectedPayers}
              onPayerChange={(payers) =>
                setFilterState((prev) => ({ ...prev, payers }))
              }
            />
          </AccordionFilterItem>
          <AccordionFilterItem
            itemName="space-type"
            label="Space Types"
            badgeLabel={selectedSpaceTypes.length}
          >
            <SpaceTypeFilter
              selectedSpaceTypeIds={selectedSpaceTypes}
              onSpaceTypeChange={(spaceTypes) =>
                setFilterState((prev) => ({ ...prev, spaceTypes }))
              }
            />
          </AccordionFilterItem>
          <AccordionFilterItem
            itemName="service-type"
            label="Service Types"
            badgeLabel={selectedServiceTypes.length}
          >
            <ServiceTypeFilter
              selectedServiceTypeIds={selectedServiceTypes}
              onServiceTypeChange={(serviceTypes) =>
                setFilterState((prev) => ({ ...prev, serviceTypes }))
              }
            />
          </AccordionFilterItem>
          <AccordionFilterItem
            itemName="county"
            label="Counties"
            badgeLabel={selectedCounties.length}
          >
            <CountiesFilter
              selectedCounties={selectedCounties}
              onCountyChange={(counties) =>
                setFilterState((prev) => ({ ...prev, counties }))
              }
            />
          </AccordionFilterItem>
          <AccordionFilterItem
            itemName="zip-code"
            label="Zip Codes"
            badgeLabel={selectedZipCodes.length}
          >
            <ZipCodesFilter
              selectedZipCodes={selectedZipCodes}
              onZipCodeChange={(zipCodes) =>
                setFilterState((prev) => ({ ...prev, zipCodes }))
              }
            />
          </AccordionFilterItem>
          <AccordionFilterItem
            itemName="tag"
            label="Trip Tags"
            badgeLabel={selectedTags.length}
          >
            <TagFilter
              selectedTags={selectedTags}
              onTagChange={(tags) =>
                setFilterState((prev) => ({ ...prev, tags }))
              }
              dataTestId="unassigned-trips-tag-filter"
            />
          </AccordionFilterItem>
        </div>
      </div>
    </FilterPopover>
  );
}

function formatTimeRangeFilterValue(timeRange: TimeRange) {
  if (timeRange.startTime && timeRange.endTime) {
    return `${formatTimeFrom24HourTo12Hour(
      timeRange.startTime
    )} - ${formatTimeFrom24HourTo12Hour(timeRange.endTime)}`;
  }

  if (timeRange.startTime) {
    return `After ${formatTimeFrom24HourTo12Hour(timeRange.startTime)}`;
  }

  if (timeRange.endTime) {
    return `Before ${formatTimeFrom24HourTo12Hour(timeRange.endTime)}`;
  }

  return 'All Day';
}
